<script setup lang="ts">
// Variants encode an action's role on the surface — prominence tracks the
// surface's primary verb, never the action's mere existence:
//
// - Filled brand (v-teal / v-blue): the surface's one primary verb —
//   deal-funnel CTAs (Add Commitment), rail zero-state primaries (Open
//   Deal, Add Investments, View Investments), wizard/auth full-width CTAs.
//   At most one filled CTA per action column.
// - Outline brand (v-teal-outline): completion/repair verbs inside content
//   sections — profile empty-state CTAs (Add Summary, Upload Deck, Add Team
//   Members), Re-upload. Branded enough to read as an action, quiet enough
//   not to compete with the primary verb.
// - Secondary (white): evergreen chrome available at all times — Edit,
//   Download, Present, Close Deal, View Commitments.
//
// Choose by region, not by mood: a section-stack body never carries a
// filled CTA while its rail exists; the rail never demotes its primary to
// outline.
import { computed, ref } from 'vue'
import { VLoading } from '@/modules/shared/components'

const props = withDefaults(
  defineProps<{
    active?: boolean
    click?: any
    cursor?: string
    disabled?: boolean
    loading?: boolean
    size?: string
    type?: 'button' | 'submit'
    variant?: string
    tooltip?: string
  }>(),
  {
    active: false,
    click: null,
    cursor: 'pointer',
    disabled: false,
    loading: undefined,
    size: 'md',
    type: 'button',
    variant: 'secondary',
    tooltip: '',
  },
)

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const internalLoading = ref(false)
const is_loading = computed(() => (props.loading !== undefined ? props.loading : internalLoading.value))

const on_click = async () => {
  if (props.click === null) {
    return
  }

  try {
    internalLoading.value = true
    await props.click()
  } finally {
    internalLoading.value = false
  }
}

const disabled = computed(() => props.disabled || is_loading.value)

///////////////////////////////////////////////////////////////////////////////
// Styling
///////////////////////////////////////////////////////////////////////////////

const base =
  // Always-on transition (colors, shadow, transform): hover-gating it
  // (hover:transition-shadow) animated changes on enter but snapped them
  // on exit, so a pointer grazing a small button replayed the one-way
  // animation — the classic flicker. active:transition-none keeps the
  // press translate instant.
  'relative block rounded-md whitespace-nowrap transition active:transition-none active:translate-y-px'

// prettier-ignore
const cursors = {
  'disabled': 'cursor-not-allowed',
  'move': 'cursor-move',
  'pointer': 'cursor-pointer',
}

// prettier-ignore
const base_variants = {
    'invisible': 'rounded-md font-medium text-gray-600 hover:bg-gray-100 active:bg-gray-200 active:translate-y-0',
    'primary': 'elevation-low bg-gray-800 font-medium text-white hover:bg-gray-700',
    'secondary': 'elevation-low bg-white font-medium text-gray-700 hover:bg-gray-50',
    // Fully-round icon-button shape (deck stage nav, 3138 design pass) —
    // !rounded-full overrides the base rounded-md; elevation-low is the
    // closest lift utility to the old --cp-shadow-md. Border-less like
    // secondary — the shadow alone separates it from the slide beneath.
    'round': 'elevation-low !rounded-full bg-white font-medium text-[var(--cp-primary,#1E3448)] hover:bg-white',
    'v-blue': 'elevation-glow border border-[#2f7294] bg-[#3b88af] font-medium text-white [--glow-color:59_136_175]',
  'v-teal': 'elevation-glow border border-[#256F74] bg-[#2E8B91] font-medium text-white [--glow-color:46_139_145]',
  'v-green': 'elevation-glow border border-[#367a7d] bg-[#408F93] font-medium text-white [--glow-color:64_143_147]',
  'v-red': 'elevation-low bg-red-600 font-medium text-white',
  // White/outline variants carry their accent as a border instead of a
  // fill. The stack keeps its tier but takes the accent's hue through
  // --shadow-color (the token every elevation layer reads), so the ring
  // edge and stacked layers agree with the border instead of staying
  // neutral gray — the same one-hue grammar the glow variants follow.
  'v-white': 'elevation-low border border-[#3b88af] bg-white font-medium text-[#3b88af] [--shadow-color:200deg_45%_25%]',
  'v-blue-outline': 'elevation-low border border-[#2f7294] bg-white font-medium text-[#2f7294] hover:bg-[#f2f7fa] [--shadow-color:200deg_45%_25%]',
  'v-teal-outline': 'elevation-low border border-[#2E8B91] bg-white font-medium text-[#2E8B91] hover:bg-[#edf6f7] [--shadow-color:184deg_45%_28%]',
  // Destructive verbs (main): filled carries the red; the ghost reads as a
  // quiet destructive affordance inside danger zones.
  'danger': 'elevation-low border-none !rounded-lg !px-5 !py-2.5 text-sm font-bold text-white bg-[#C0392B]',
  'danger-ghost': 'border-none !rounded-lg !px-3 !py-2.5 text-sm font-semibold text-[#C0392B] hover:bg-[#FDEDEB] w-full',
}

// array is when [false, true] is passed in as "active"
const active_variants = {
  secondary: ['ring-1 ring-gray-700/20', 'ring-1 ring-[#3b88af] hover:ring-[#3b88af]'],
}

// prettier-ignore
const disabled_variants = {
  invisible: 'active:!translate-y-0 text-gray-400 opacity-50 active:bg-transparent hover:bg-transparent',
  primary: 'active:!translate-y-0 bg-gray-200 text-gray-400 opacity-50',
  secondary: 'active:!translate-y-0 bg-gray-200 text-gray-400 opacity-50 ring-1 ring-gray-200/20',
  'v-blue': 'active:!translate-y-0 opacity-50',
  'v-teal': 'active:!translate-y-0 opacity-50',
  'v-blue-outline':
    'active:!translate-y-0 !border-gray-300 !bg-white !text-gray-400 hover:!bg-white opacity-50',
  'v-teal-outline':
    'active:!translate-y-0 !border-gray-300 !bg-white !text-gray-400 hover:!bg-white opacity-50',
  danger: 'active:!translate-y-0 bg-[#E3B3AC] text-white hover:bg-[#E3B3AC]',
  'danger-ghost':
    'active:!translate-y-0 text-[#C0392B] opacity-50 hover:bg-transparent',
}

const sizes = {
  xs: 'px-2 py-1 text-xs',
  'xs-narrow': 'px-1 py-1 text-xs',
  // The rail row-button treatment (tickets/3185): taller rows for
  // breathing room, semibold against the variant's medium. Promoted from
  // the !-override pattern the commitment card used to carry per use.
  'xs-tall': 'px-2 py-2 text-xs font-semibold',
  sm: 'px-2 py-1 text-sm',
  md: 'sm:px-2.5 sm:py-1.5 sm:text-sm px-2 py-1 text-sm',
  lg: 'px-3 py-2 text-sm',
  xl: 'px-3.5 py-2.5 text-sm',
  '2xl': 'px-6 py-3 text-base',
}

const classes = computed(() => {
  const cursorClass = cursors[props.cursor] || ''
  const variantClass = base_variants[props.variant] || ''
  const sizeClass = sizes[props.size] || ''

  const classList = [base, variantClass, sizeClass]

  try {
    if (props.active) {
      classList.push(active_variants[props.variant][1])
    } else {
      classList.push(active_variants[props.variant][0])
    }
  } catch (e) {
    // do nothing
  }

  if (disabled.value) {
    classList.push(disabled_variants[props.variant])
    classList.push('cursor-not-allowed') // To keep consistency with action-items
  } else {
    classList.push(cursorClass)
  }

  return classList
})
</script>

<template>
  <button :type="type" :class="classes" :disabled="disabled || is_loading" @click="on_click" :title="tooltip">
    <div :class="is_loading ? 'invisible' : 'visible'">
      <slot></slot>
    </div>

    <template v-if="is_loading">
      <div class="absolute inset-0 flex items-center justify-center">
        <slot name="loading"><VLoading /></slot>
      </div>
    </template>
  </button>
</template>
